import type {HeaderData} from '@commercial-website/data-layer'
import {GatsbyClient} from '@commercial-website/data-layer/client'
import {LegacyButton as Button, cn} from '@commercial-website/ui'
import {graphql, useStaticQuery} from 'gatsby'
import React from 'react'

import {topNavigationSignupActioned, TopNavigationSignupActionedAction} from '@/analytics/segment'
import {LocalizedLink, TrackedLocalizedLink} from '@/components/localized-link'
import type {Props as SignupProps} from '@/components/signup-modal'
import type {HeaderQuery} from '@/graphql-types'
import useHasMounted from '@/hooks/use-has-mounted'
import {useLocalizedContent} from '@/hooks/use-localized-content'
import {useLocale} from '@/providers/locale'
import {usePageInfo} from '@/providers/page-info'
import {useSignupModal} from '@/providers/signup-modal'

import {HeaderV2} from './header-v2'
import {mapPrismicHeaderV2} from './map-prismic-header-v2'

const AVAILABILITY_TEST_ID = 'availability-test'

export interface Props {
    hideLeftNav?: boolean
    hideRightNav?: boolean
    hideHeader?: boolean
    ctaDestination?: 'signupform' | 'demopage' | 'partnersform' | 'accountingpartners'
    exploreProductDestination?: 'explorepartnerportal' | 'exploreproduct'
    genericBookADemo?: boolean
}

const TRACKED_ELEMENT_LOCATION = 'navbar header'
const CTA_HOVER_TRANSITION = 'transition-[background-color] duration-130 ease-in'

type CtaButtonParams = {
    ctaDestination: Props['ctaDestination']
    currentLocale: {code: string}
    text: string
    location: {pathname: string} | null | undefined
    onSignupCtaClick: (signupProps?: SignupProps) => void
    className?: string
    disabled?: boolean
}

const buildCtaButton = ({
    ctaDestination,
    currentLocale,
    text,
    location,
    onSignupCtaClick,
    disabled,
    className,
}: CtaButtonParams): React.ReactElement => {
    if (currentLocale.code === 'en-us') {
        return (
            <Button
                className={cn(className, CTA_HOVER_TRANSITION)}
                render={(props) => (
                    <TrackedLocalizedLink
                        to="/getting-started"
                        trackingData={{
                            linkText: text,
                            linkType: 'cta',
                            elementLocation: TRACKED_ELEMENT_LOCATION,
                        }}
                        state={{prevPath: location?.pathname}}
                        {...props}
                    />
                )}
            >
                {text}
            </Button>
        )
    }

    switch (ctaDestination) {
        case 'signupform': {
            return (
                <Button
                    className={cn(className, CTA_HOVER_TRANSITION)}
                    onClick={() => onSignupCtaClick()}
                    aria-disabled={disabled}
                >
                    {text}
                </Button>
            )
        }

        case 'accountingpartners': {
            return (
                <Button
                    className={cn(className, CTA_HOVER_TRANSITION)}
                    onClick={() =>
                        onSignupCtaClick({
                            signupProps: {
                                form: 'Partner',
                                isPartner: true,
                            },
                        })
                    }
                    aria-disabled={disabled}
                >
                    {text}
                </Button>
            )
        }

        case 'demopage': {
            return (
                <Button
                    className={cn(className, CTA_HOVER_TRANSITION)}
                    render={(props) => (
                        <TrackedLocalizedLink
                            to="/demo"
                            trackingData={{
                                linkText: text,
                                linkType: 'cta',
                                elementLocation: TRACKED_ELEMENT_LOCATION,
                            }}
                            {...props}
                        />
                    )}
                >
                    {text}
                </Button>
            )
        }

        case 'partnersform': {
            return (
                <Button
                    className={cn(className, CTA_HOVER_TRANSITION)}
                    render={(props) => (
                        <LocalizedLink
                            to="/bigform-partner"
                            onClick={() =>
                                topNavigationSignupActioned({
                                    action: TopNavigationSignupActionedAction.Partners,
                                })
                            }
                            {...props}
                        />
                    )}
                >
                    {text}
                </Button>
            )
        }

        default: {
            throw new Error('Unrecognized destination')
        }
    }
}

const HeaderContainer = ({hideHeader, ...props}: Props) => {
    const data = useStaticQuery<HeaderQuery>(query)
    const [content] = useLocalizedContent(data.allPrismicHeader.nodes)
    const [contentV2] = useLocalizedContent(data.allPrismicHeaderV2.nodes, {
        fallbackToDefaultLocale: false,
    })

    const hasMounted = useHasMounted()
    const currentLocale = useLocale()
    const {location} = usePageInfo()
    const {renderSignupModal} = useSignupModal()

    // GatsbyClient.getHeader throws when a secondary locale has no static content, which replaces the
    // explicit guard that used to live here.
    const secondaryHeader = currentLocale.isSecondary
        ? GatsbyClient.getHeader(currentLocale.code)
        : undefined

    if (!currentLocale.isSecondary && !contentV2) {
        throw new Error(`Missing Prismic HeaderV2 content for locale ${currentLocale.code}`)
    }

    const headerV2Content: HeaderData = secondaryHeader ?? mapPrismicHeaderV2(contentV2!.data)

    const ctaButtonText = secondaryHeader
        ? secondaryHeader.ctaButtonText
        : content.data.start_free_trial

    const onSignupCtaClick = (signupProps?: SignupProps) => {
        renderSignupModal(signupProps)
        topNavigationSignupActioned({action: TopNavigationSignupActionedAction.Started})
    }

    if (hideHeader) {
        return null
    }

    return (
        <HeaderV2
            content={headerV2Content}
            ctaButton={({className}) =>
                buildCtaButton({
                    ctaDestination: props.ctaDestination ?? 'signupform',
                    currentLocale,
                    text: ctaButtonText,
                    location,
                    onSignupCtaClick,
                    className,
                    disabled: !hasMounted,
                })
            }
            hideLeftNav={props.hideLeftNav}
            hideRightNav={props.hideRightNav}
            testId={AVAILABILITY_TEST_ID}
        />
    )
}

const query = graphql`
    query Header {
        allPrismicHeader {
            nodes {
                lang
                data {
                    start_free_trial
                }
            }
        }
        allPrismicHeaderV2 {
            nodes {
                lang
                data {
                    logInText: log_in
                    secondaryCtaText: secondary_cta_text
                    secondaryCtaLink: secondary_cta_link {
                        ...PrismicLink
                    }
                    noResultsText: no_results_text
                    items {
                        label
                        link {
                            ...PrismicLink
                        }
                        dropdown {
                            document {
                                ... on PrismicHeaderV2Dropdown {
                                    data {
                                        body {
                                            ... on PrismicHeaderV2DropdownDataBodyLinksGroup {
                                                primary {
                                                    label
                                                }
                                                items {
                                                    text
                                                    sub_text
                                                    link {
                                                        ...PrismicLink
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        hide_on
                    }
                }
            }
        }
    }
`

export default HeaderContainer
